/**
 * Decides who may stay on the reusable invite link page (`/onboard/:advisorId`,
 * AdvisorPublicInvite) once their session has loaded.
 *
 * - Signed out: stays, and fills in the form.
 * - Signed-in client: stays. They may be starting an assessment for someone else who shares their
 *   email, which the page's returning-email choice step handles. (They used to be sent to
 *   `/client/home`, which left people unable to start a second assessment.)
 * - Advisor or wizard: stays; the page shows them the "This link is for clients" screen.
 * - Any other signed-in session type: sent to `/`.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionType } from '@shared';

const SESSION_TYPES_THAT_STAY = [
  SessionType.unknown,
  SessionType.client,
  SessionType.advisor,
  SessionType.wizard,
];

export function usePublicInviteSessionRedirect({
  isAuthLoading,
  isAuthenticated,
  sessionType,
}: {
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  sessionType: SessionType;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || SESSION_TYPES_THAT_STAY.includes(sessionType)) {
      return;
    }
    navigate('/', { replace: true });
  }, [isAuthLoading, isAuthenticated, sessionType, navigate]);
}
